/**
 * Lectura de los parametros de la sesion de checkout (plan 7.3.2).
 *
 * El SDK monta el iframe en `/session?pk=<pk_>&locale=<locale>` con el
 * `client_secret` y el `nonce` en el FRAGMENT (`#client_secret=...&nonce=...`).
 * El fragment nunca viaja al servidor ni aparece en la cabecera Referer, pero SI
 * queda en `location.href` y en la entrada del historial: por eso se borra con
 * `history.replaceState()` inmediatamente despues de leerlo.
 *
 * HAY UN SEGUNDO CAMINO, y no es una alternativa sino un hueco que tapar: la vuelta de
 * `skip_hosted_checkout` llega con el secreto en la QUERY (`&cs=...`), porque la pasarela rechaza
 * una `return_url` con almohadilla. Ver `QUERY_SECRET_PARAM`.
 *
 * Y por esa misma query llega la MARCA DE RETORNO (`&gw_return=1`), que dice que esta carga es la
 * vuelta de una pasarela y no una visita nueva. Ver `GATEWAY_RETURN_PARAM`: es lo unico que
 * sobrevive a volver en otra pestana, que es lo que hace el movil.
 *
 * Y una SEGUNDA forma de decir lo mismo, que no la escribimos nosotros: el `&resultIndicator=` con
 * el que BCI devuelve al comprador tras aprobar el pago. Hace falta porque su SDK sustituye nuestra
 * `return_url` por la de la pagina padre, con lo que la marca de arriba no llega nunca. Ver
 * `GATEWAY_RESULT_INDICATOR_PARAM`.
 */
import type { Locale } from '../i18n';
import { resolveLocale } from '../i18n';

/** Misma regex que `nonce.ts` de @apipay/checkout-js: 16 bytes en hex. */
const NONCE_PATTERN = /^[0-9a-f]{32}$/;

/**
 * `pi_<id>_secret_<random>`, el formato que emite payment-api.
 *
 * La parte aleatoria es BASE64URL, asi que su alfabeto incluye `-` y `_` ademas de letras y
 * digitos: `PaymentIntentService.mintClientSecret` la genera con
 * `Base64.getUrlEncoder().withoutPadding()` sobre 24 bytes. Exigir solo alfanumericos rechazaba
 * cerca de dos tercios de los secretos legitimos —la probabilidad de que 32 caracteres base64url
 * no traigan ningun `-` ni `_` es del 36 %— y la sesion moria en silencio: sin client_secret el
 * widget no llega a pedir el intent, no anuncia `ready`, y el SDK acaba mostrando el timeout.
 * El prefijo `pi_` sigue siendo base62, por eso su clase de caracteres no cambia.
 */
const CLIENT_SECRET_PATTERN = /^pi_[A-Za-z0-9]{1,64}_secret_[A-Za-z0-9_-]{1,64}$/;

/** Clave publicable; una sk_ jamas debe llegar al navegador. */
const PUBLIC_KEY_PATTERN = /^pk_(live|test)_[A-Za-z0-9]+$/;

const SECRET_SEPARATOR = '_secret_';

export interface SessionFragment {
  clientSecret: string | null;
  nonce: string | null;
  /**
   * Esta carga es la VUELTA de una pasarela, y lo dice la URL (ver {@link GATEWAY_RETURN_PARAM}).
   *
   * <p>Vive aqui, y no en {@link SessionQuery}, porque comparte con el `client_secret` la unica
   * propiedad que importa: <strong>se lee una sola vez y se borra de la barra de direcciones en el
   * mismo gesto</strong>. Separarla la sacaria de ese gesto y quien la leyera despues encontraria la
   * URL ya limpia.
   *
   * <p>Lo dicen DOS parametros y basta con cualquiera de los dos: la marca que ponemos nosotros
   * ({@link GATEWAY_RETURN_PARAM}) y el indicador que pone BCI
   * ({@link GATEWAY_RESULT_INDICATOR_PARAM}). El segundo hace falta porque en BCI la nuestra no
   * llega: su SDK sobrescribe la `returnUrl` que le damos con la URL de la pagina padre, asi que de
   * la `return_url` que compone la plataforma no sobrevive nada.
   */
  gatewayReturn: boolean;
  /**
   * Valor del indicador con el que la pasarela dice que el pago SALIO BIEN, cuando lo trae.
   *
   * <p>Hoy solo lo trae BCI. No decide nada aqui --ni puede: llega por el navegador-- y viaja al
   * backend en el confirm para que lo cruce con la copia que guardo al crear la sesion. Ver
   * {@link GATEWAY_RESULT_INDICATOR_PARAM}.
   */
  gatewayResultIndicator: string | null;
}

export interface SessionQuery {
  publicKey: string | null;
  locale: Locale;
}

/** Valida el formato del nonce antes de usarlo en el canal postMessage. */
export function isValidSessionNonce(value: unknown): value is string {
  return typeof value === 'string' && NONCE_PATTERN.test(value);
}

/**
 * El id del intent esta embebido en el client_secret (`pi_<id>_secret_<random>`),
 * asi que no hace falta un parametro extra ni una llamada previa a la API.
 */
export function intentIdFromClientSecret(clientSecret: string): string | null {
  const index = clientSecret.indexOf(SECRET_SEPARATOR);
  return index > 0 ? clientSecret.slice(0, index) : null;
}

/**
 * Parametro por el que llega el `client_secret` cuando NO puede llegar por el fragment.
 *
 * <p>Pasa en la vuelta de `skip_hosted_checkout`: ahi el comprador no paso por esta pagina a la
 * ida --el comercio lo mando directo a la pasarela-- asi que no hay nada recordado que recuperar, y
 * la sesion tiene que venir en la URL de retorno. Esa URL la escribe la PASARELA en su formulario de
 * vuelta, y **Transbank rechaza la que lleva almohadilla**: `HTTP 422: Parameter TBK_URL_RETURN
 * rejected`, medido contra QA el 2026-09-08, con lo que ese modo respondia 402 siempre.
 *
 * <p>Se acepta a sabiendas de lo que cuesta y de lo que no: el nginx de este checkout registra
 * `$uri` sin query y emite `Referrer-Policy: no-referrer`, asi que el secreto no queda en su access
 * log ni sale en un Referer. Lo que si hace falta es quitarlo de la URL en cuanto se lee, y de eso
 * se encarga `clearSecrets`.
 */
const QUERY_SECRET_PARAM = 'cs';

/**
 * Parametro por el que llega el `client_secret` en el camino NORMAL: dentro del fragment.
 *
 * <p>Lo escriben DOS productores con la misma cadena y ninguno de los dos la deduce del otro:
 * `HostedCheckoutUrl#forIntent` en la plataforma --la URL que se le entrega al comercio para la
 * ida-- y el SDK al montar el iframe. Es la gemela de {@link QUERY_SECRET_PARAM}, que cubre la
 * vuelta; tienen nombres distintos a proposito, asi que cada una necesita su propia fila en la tabla
 * de pares de `parseFragment.test.ts`.
 *
 * <p>Tenia nombre en NINGUN lado hasta el 2026-09-16: era un literal `'client_secret'` aqui y otro
 * `"#client_secret="` en Java. Sin simbolo, cambiarlo en un lado no tocaba nada del otro y la unica
 * senal era un comprador viendo «No pudimos iniciar la sesion de pago».
 */
const FRAGMENT_SECRET_PARAM = 'client_secret';

/**
 * Parametro de la clave publicable, la MISMA cadena que `HostedCheckoutUrl#PUBLIC_KEY_PARAM`.
 *
 * <p>Va en las dos URLs que compone la plataforma porque sale de su tronco comun, y sin el esta
 * pagina no puede autenticarse contra la API: no llega ni a pedir el intent. Que ademas se valide
 * contra {@link PUBLIC_KEY_PATTERN} no ayuda si el nombre se separa --una `pk` que no se encuentra y
 * una `pk` mal formada acaban las dos en la misma pantalla de sesion invalida--, y por eso hace
 * falta atarlo aqui y no confiar en la validacion.
 */
const PUBLIC_KEY_PARAM = 'pk';

/**
 * Marca de que esta carga es la VUELTA de una pasarela. La escribe la plataforma en la `return_url`
 * (`HostedCheckoutUrl#GATEWAY_RETURN_PARAM`) y no la escribe nunca en la URL de la ida.
 *
 * <p>EXISTE POR UN COBRO MEDIDO EN QA EL 2026-09-16: `pi_shfZPUkbg9GzZLYMkHAxA1Nb`. El comprador
 * pago con RutPay en la app del banco, volvio al checkout, y el checkout **le ofrecio pagar otra
 * vez**; al pulsar, saltaba de nuevo a la app, que respondia «ya pagado». Es lo mas peligroso que
 * puede hacer un checkout.
 *
 * <p>La causa es de pestanas. El widget reconocia el retorno leyendo la salida que habia apuntado en
 * `sessionStorage` antes de saltar (ver `recallPendingRedirect`), y `sessionStorage` es POR PESTANA:
 * en movil el pago sale hacia la app del banco y el comprador vuelve en OTRA pestana, donde no hay
 * nada apuntado. El widget lo leia como una primera visita.
 *
 * <p>Es la segunda mitad del mismo problema que puso el secreto en la query: aquello arreglo
 * IDENTIFICAR la sesion, esto arregla RECONOCER el retorno. Lo unico que cruza de una pestana a otra
 * es la propia URL.
 *
 * <p>El `sessionStorage` NO se quita: en escritorio funciona, y ademas dice QUE pasarela, que es
 * informacion que esta marca no lleva.
 */
const GATEWAY_RETURN_PARAM = 'gw_return';

/** Unico valor aceptado. Se compara tal cual: la presencia sola no basta. */
const GATEWAY_RETURN_VALUE = '1';

/**
 * Parametro con el que BCI Hosted Checkout devuelve al comprador cuando el pago SE APROBO.
 *
 * <p>Lo escribe la pasarela, no nosotros, y es la mitad que faltaba del flujo que su fabricante
 * documenta: *«You can determine the success of the payment by comparing the resultIndicator
 * parameter to the successIndicator parameter returned in the INITIATE CHECKOUT response»*, y su
 * referencia de campos anade que solo se devuelve **«for successful payments only»**.
 *
 * <p><strong>Llevaba funcionando todo el tiempo y esta pagina no lo leia.</strong> Medido en QA el
 * 2026-09-21 tras aprobar un pago real, la barra de direcciones decia
 * `/session?pk=pk_test_...&resultIndicator=2a8fc1e41acc4184&sessionVersion=...`. Mientras tanto el
 * desenlace se esperaba del sondeo escalonado, que tarda minutos.
 *
 * <p>Y explica por que el callback del SDK tampoco avisaba: su `checkout.min.js` solo autodetecta el
 * retorno cuando viene en el FRAGMENTO (`#__hc-action-complete-...`). El nuestro viene en la QUERY,
 * que es la forma de `interaction.returnUrl`, y por ahi el SDK no mira.
 *
 * <p><strong>Esta cadena esta escrita DOS VECES</strong>, aqui y en `BciGateway.java`
 * (`PARAM_RESULT_INDICATOR`), y nada las ata salvo el guardian de correspondencia de
 * `BciHostedCheckoutFlow.test.tsx`. Sin el, un renombrado en cualquiera de los dos lados deja el
 * contraste del backend sin dispararse nunca y las dos suites en verde.
 *
 * <p>Se lee y se BORRA como el secreto y como la marca de retorno, y por el mismo motivo practico:
 * esta pantalla se remonta sola varias veces por cobro y la vuelta se apunta en `sessionStorage` en
 * cuanto se lee. Dejarlo en la barra de direcciones solo serviria para que una recarga pareciera una
 * vuelta nueva.
 */
export const GATEWAY_RESULT_INDICATOR_PARAM = 'resultIndicator';

/**
 * Borra de la URL lo que solo puede leerse UNA vez: las dos formas del secreto y la marca de
 * retorno. No toca nada mas.
 *
 * <p>El resto de la query se conserva ENTERA a proposito: ahi viajan la `pk`, con la que la pagina
 * se autentica, y el `token_ws` del retorno de Transbank, que es lo unico con lo que se cierra el
 * cobro. Borrar la query de un tajo para esconder el secreto dejaria el pago sin commitear con el
 * dinero ya retenido al otro lado.
 *
 * <p>⚠️ Se llama DESPUES de leer, nunca antes: si la marca de retorno se borrara primero, el widget
 * volveria al punto de partida --una visita nueva, con su boton de pagar-- que es justo el defecto
 * que la marca viene a cerrar.
 */
function clearOneShotParams(win: Window): void {
  const { pathname, search, hash } = win.location;
  const query = new URLSearchParams(search);
  if (
    !query.has(QUERY_SECRET_PARAM) &&
    !query.has(GATEWAY_RETURN_PARAM) &&
    !query.has(GATEWAY_RESULT_INDICATOR_PARAM) &&
    hash.length === 0
  ) {
    return;
  }
  query.delete(QUERY_SECRET_PARAM);
  query.delete(GATEWAY_RETURN_PARAM);
  query.delete(GATEWAY_RESULT_INDICATOR_PARAM);
  const restante = query.toString();
  try {
    win.history.replaceState(null, '', `${pathname}${restante.length > 0 ? `?${restante}` : ''}`);
  } catch {
    // Si el navegador bloquea replaceState (iframe sin allow-same-origin) al menos
    // se vacia el hash: el client_secret no puede quedar en la barra de direcciones.
    win.location.hash = '';
  }
}

/**
 * Extrae `client_secret` y `nonce` del fragment y LO BORRA. Es idempotente en el
 * sentido de que una segunda llamada devuelve nulls: el llamador debe invocarla una
 * sola vez, en el bootstrap, y propagar el resultado por props.
 */
export function parseFragment(win: Window = window): SessionFragment {
  const rawHash = win.location.hash;
  const raw = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
  const params = new URLSearchParams(raw);
  const nonce = params.get('nonce');
  const query = new URLSearchParams(win.location.search);

  // El fragment MANDA sobre la query: si vienen los dos, el de la almohadilla es el de esta carga.
  // La query solo cubre el hueco de la vuelta de skip_hosted_checkout (ver QUERY_SECRET_PARAM).
  const clientSecret = params.get(FRAGMENT_SECRET_PARAM) ?? query.get(QUERY_SECRET_PARAM);

  // LEER ANTES DE BORRAR, y las dos cosas en el mismo gesto: ver `clearOneShotParams`.
  const marcaPropia = query.get(GATEWAY_RETURN_PARAM) === GATEWAY_RETURN_VALUE;
  const indicador = query.get(GATEWAY_RESULT_INDICATOR_PARAM);
  const gatewayResultIndicator = indicador !== null && indicador.length > 0 ? indicador : null;

  clearOneShotParams(win);

  return {
    clientSecret:
      clientSecret !== null && CLIENT_SECRET_PATTERN.test(clientSecret) ? clientSecret : null,
    nonce: isValidSessionNonce(nonce) ? nonce : null,
    // CUALQUIERA DE LAS DOS basta, y hacen falta las dos vias. La marca propia es la de las
    // pasarelas que respetan nuestra `return_url`; el indicador es la unica senal que sobrevive en
    // BCI, cuyo SDK sustituye esa URL por la de la pagina padre. Ver `GATEWAY_RESULT_INDICATOR_PARAM`.
    gatewayReturn: marcaPropia || gatewayResultIndicator !== null,
    gatewayResultIndicator,
  };
}

/** Lee `pk` y `locale` del querystring; ambos son publicos y pueden quedar en la URL. */
export function parseSessionQuery(win: Window = window): SessionQuery {
  const params = new URLSearchParams(win.location.search);
  const publicKey = params.get(PUBLIC_KEY_PARAM);
  return {
    publicKey: publicKey !== null && PUBLIC_KEY_PATTERN.test(publicKey) ? publicKey : null,
    locale: resolveLocale(params.get('locale')),
  };
}
